import dayjs from 'dayjs';
import { convertHtmlToText } from 'src/helpers/helpers';
import { CampaignConfigSchema } from '../../types';
import { CampaignFormValues, FormMessage } from '../../types';
import { isFieldRequired } from '../../helpers/configHelpers';
import { validateMessageOrder } from './messageSchedule';

/**
 * The individual predicates behind the primary action's disabled state. Each
 * policy composes the ones its own campaign type needs.
 */

/** Which config message template governs a message index. */
function templateForIndex(config: CampaignConfigSchema, index: number) {
  const templates = config.messageTemplates ?? [];
  if (templates.length === 0) return undefined;
  if (templates.length === 1) return templates[0];
  if (index < templates.length - 1) return templates[index];
  return templates[templates.length - 1];
}

/**
 * A message whose text field is visible must carry text or an attachment — an
 * image-only MMS is valid without copy. `skipFirst` covers flyers, whose first
 * message is the image.
 */
function hasEmptyMessageText(
  values: CampaignFormValues,
  config: CampaignConfigSchema,
  options: { skipFirst?: boolean } = {},
): boolean {
  return (values.scheduledMessages ?? []).some((message, index) => {
    if (options.skipFirst && message.position === 1) return false;

    const template = templateForIndex(config, index);
    const isTextVisible = template ? !!template.mmsFieldsVisibility?.text : true;
    const hasContent =
      !!convertHtmlToText(message.text ?? '').trim() || !!message.links?.length;

    return isTextVisible && !hasContent;
  });
}

/** A capture-response message must say which field it captures into. */
export function hasCaptureErrors(values: CampaignFormValues): boolean {
  return (values.scheduledMessages ?? []).some(m => m.isCaptureContact && !m.dataCapture);
}

/** A time-scheduled message must have a time. */
export function hasMissingScheduledTime(values: CampaignFormValues): boolean {
  return (values.scheduledMessages ?? []).some(m => m.scheduledType === 'time' && !m.time);
}

/** No audience selected. */
export function hasMissingGroups(values: CampaignFormValues): boolean {
  return !(values.groupIds ?? []).length;
}

/** A keyword is required by the config but none is selected. */
export function hasMissingRequiredKeywords(
  values: CampaignFormValues,
  config: CampaignConfigSchema,
): boolean {
  return isFieldRequired(config.keywords) && !(values.keywordIds ?? []).length;
}

/** Messages must be in chronological order. */
export function hasScheduleOrderErrors(values: CampaignFormValues): boolean {
  const messages = values.scheduledMessages ?? [];
  if (!messages.length) return false;
  return !validateMessageOrder(messages as FormMessage[], values.dateScheduled).valid;
}

/** A delay-scheduled message must carry a parseable delay. */
export function hasInvalidReminderDelay(values: CampaignFormValues): boolean {
  return (values.scheduledMessages ?? []).some(
    m => m.scheduledType === 'delay'
      && (!m.delay || m.delay.length === 1 || m.delay.includes('NaN')),
  );
}

/** Any generic delay option needs a complete, parseable value. */
export const hasInvalidScheduledDelay = hasInvalidReminderDelay;

/** Annual schedules every message off the contact date, so all of them need a delay. */
export function hasInvalidAnnualDelay(values: CampaignFormValues): boolean {
  return (values.scheduledMessages ?? []).some(m => !m.delay || m.delay.includes('NaN'));
}

/** Flyers need an attachment on the first message and text on every other one. */
export function hasInvalidFlyerMessages(values: CampaignFormValues): boolean {
  const messages = values.scheduledMessages ?? [];
  const first = messages[0];
  if (!first?.links?.length) return true;
  return !messages.slice(1).every(m => !!m.text);
}

/** Enrollment join window: both bounds required, end strictly after start. */
export function hasJoinWindowErrors(values: CampaignFormValues): boolean {
  const { joinStartAt, joinEndAt } = values;
  if (!joinStartAt || !joinEndAt) return true;

  const start = dayjs(joinStartAt);
  const end = dayjs(joinEndAt);
  if (!start.isValid() || !end.isValid()) return true;

  return !end.isAfter(start);
}

/** Drafts may omit the window, but cannot store invalid or reversed values. */
export function hasInvalidJoinWindowValues(values: CampaignFormValues): boolean {
  const { joinStartAt, joinEndAt } = values;
  if (!joinStartAt || !joinEndAt) return false;
  const start = dayjs(joinStartAt);
  const end = dayjs(joinEndAt);
  return !start.isValid() || !end.isValid() || !end.isAfter(start);
}

/**
 * The checks that apply to every campaign type before its own branch runs.
 */
export function hasBaseErrors(
  values: CampaignFormValues,
  config: CampaignConfigSchema,
  options: { skipFirstMessageText?: boolean } = {},
): boolean {
  return (
    !values.name || hasEmptyMessageText(values, config, { skipFirst: options.skipFirstMessageText })
  );
}
